import { useState } from "react";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Tooltip, Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { REWRITER_FIXABLE_CHECKS } from "./seoColors";

const _13TV_RE = /(?:^|[./])13tv\.co\.il(?:\/|$)/i;

function isThinWithVideo(report) {
  if (!report) return false;
  const pageText = report.page_text || "";
  const wordCount = pageText.trim().split(/\s+/).filter(Boolean).length;
  const checks = report.checks || [];
  const mm = checks.find(c => c.check === "Multimedia Content");
  return wordCount < 150 && mm?.status === "pass" && mm?.value?.toLowerCase().includes("video");
}

export default function ComingSoonTeaser({ text, report, onRewrite }) {
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [queueId, setQueueId] = useState(null);

  const is13tv = _13TV_RE.test(report?.url || "");
  if (!is13tv) return null;

  const isVideoMode = isThinWithVideo(report);

  const checks = report?.checks || [];
  const fixableFailCount = checks.filter(
    (c) => REWRITER_FIXABLE_CHECKS.has(c.check) && c.status === "fail"
  ).length;
  const fixableWarnCount = checks.filter(
    (c) => REWRITER_FIXABLE_CHECKS.has(c.check) && c.status === "warning"
  ).length;

  const totalImpact = report
    ? (report.priority_fixes || [])
        .filter((f) => REWRITER_FIXABLE_CHECKS.has(f.check))
        .reduce((sum, f) => sum + (f.impact_points || 0), 0)
    : 0;

  if (!isVideoMode && fixableFailCount === 0 && fixableWarnCount === 0) return null;

  const handleClick = async () => {
    if (status === "done") {
      window.history.pushState({}, "", "/queue");
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    if (status !== "idle" || !onRewrite) return;
    setStatus("loading");
    try {
      const res = await onRewrite();
      if (res?.queueId) setQueueId(res.queueId);
      setStatus("done");
    } catch (_) {
      setStatus("idle");
    }
  };

  const accentColor = "#2e7d32";
  const accentLight = "rgba(46,125,50,0.08)";
  const Icon = isVideoMode ? VideoLibraryIcon : AutoFixHighIcon;
  const t = isVideoMode ? text.video : text;

  return (
    <Card sx={{
      border: "1px solid rgba(46,125,50,0.3)",
      background: "linear-gradient(135deg, rgba(46,125,50,0.03) 0%, rgba(76,175,80,0.05) 100%)",
      mb: 2,
    }}>
      <CardContent sx={{ p: 3, pt: "22px !important" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
            bgcolor: accentColor,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>{t.title}</Typography>
            <Chip
              label={t.badge}
              size="small"
              sx={{
                height: 20, fontSize: 10, fontWeight: 700,
                bgcolor: accentLight,
                color: accentColor,
                borderRadius: "10px",
              }}
            />
          </Box>
          <Tooltip
            title={t.disclaimer}
            placement="top"
            arrow
            componentsProps={{
              tooltip: { sx: { maxWidth: 300, fontSize: "0.75rem", lineHeight: 1.6 } },
            }}
          >
            <IconButton size="small" sx={{ color: "text.disabled", "&:hover": { color: "text.secondary" } }}>
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t.platformDisclaimer}
            placement="top"
            arrow
            componentsProps={{
              tooltip: { sx: { maxWidth: 300, fontSize: "0.75rem", lineHeight: 1.6 } },
            }}
          >
            <IconButton size="small" sx={{ color: "text.disabled", "&:hover": { color: "warning.main" } }}>
              <ErrorOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 520, lineHeight: 1.7 }}>
          {t.subtitle}
        </Typography>

        {/* Stats chips — only for text mode */}
        {!isVideoMode && report && (fixableFailCount > 0 || fixableWarnCount > 0) && (
          <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
            {fixableFailCount > 0 && (
              <Chip
                label={text.failingIssues(fixableFailCount)}
                size="small"
                sx={{ bgcolor: "rgba(211,47,47,0.08)", color: "error.main", fontWeight: 600 }}
              />
            )}
            {fixableWarnCount > 0 && (
              <Chip
                label={text.warningIssues(fixableWarnCount)}
                size="small"
                sx={{ bgcolor: "rgba(245,124,0,0.08)", color: "warning.main", fontWeight: 600 }}
              />
            )}
            {totalImpact > 0 && (
              <Chip
                label={text.impactPoints(totalImpact)}
                size="small"
                sx={{ bgcolor: accentLight, color: accentColor, fontWeight: 600 }}
              />
            )}
          </Box>
        )}

        {/* CTA */}
        <Button
          variant="contained"
          disabled={status === "loading"}
          onClick={handleClick}
          startIcon={status === "loading" ? <CircularProgress size={16} color="inherit" /> : <Icon sx={{ fontSize: 18 }} />}
          sx={{ borderRadius: "20px", fontWeight: 700, bgcolor: accentColor, "&:hover": { bgcolor: "#1b5e20" } }}
        >
          {status === "done" ? t.ctaSuccess : status === "loading" ? t.ctaLoading : t.cta}
        </Button>
      </CardContent>
    </Card>
  );
}
